// WAAP Analytics → Threat Investigation — access-log DETAIL (the "Details" row
// modal). Clicking a row in the Details table fetches the full per-request
// access-log record (browser/device fingerprint, injection + threat findings,
// upstream timing — everything the Details table omits).
//   GET /analytics/threat-investigation/access-log/detail?request_uuid=…&hostname=…&timestamp_ms=…
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';

/** The full access-log record. The BE returns the vendor's flat `data` object
 *  verbatim (~100 fields), so keys mirror the upstream access-log schema
 *  (request_uuid, action, status, user_agent, inject_*, ubm_*, …). Kept as a
 *  loose bag — the modal reads the handful of fields it renders defensively. */
export type WaapAccessLogDetail = Record<string, unknown>;

/** Identity of one Details row, sourced from the row itself: `request_id`
 *  (= request_uuid) and `host` (= hostname). `timestampMs` is optional (the BE
 *  Details table exposes only a formatted time today) — the lookup keys on the
 *  globally-unique request_uuid, using hostname/timestampMs only to narrow. */
export interface WaapAccessLogArgs {
  requestUuid: string;
  hostname: string;
  timestampMs?: number;
}

/** Fetch one access-log record for the clicked Details row. Disabled until a row
 *  is selected (`args` non-null) and the modal is open (`enabled`). Returns null
 *  when the record is not found / the board is not configured (400/404/501). */
export function useWaapAccessLogDetail(
  domainId: string | undefined,
  args: WaapAccessLogArgs | null,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      ...waapKeys.aspect(domainId ?? '', WAAP_AREAS.threatInvestigationAccessLogDetail),
      args?.requestUuid,
      args?.hostname,
      args?.timestampMs,
    ] as const,
    enabled: !!domainId && !!args && enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<WaapAccessLogDetail | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const params = new URLSearchParams({ tz, request_uuid: args!.requestUuid });
        if (args!.hostname) params.set('hostname', args!.hostname);
        if (args!.timestampMs != null) params.set('timestamp_ms', String(args!.timestampMs));
        const path = waapPath(domainId as string, WAAP_AREAS.threatInvestigationAccessLogDetail);
        // The vendor's /access_log/detail ClickHouse query legitimately runs ~1-2
        // min (plus a cold WAAP SSO-session warm on the first click). The backend
        // caps its own upstream call at 120s; wait a bit longer than that so the
        // backend's response (or clean error) lands before the client aborts.
        const res = await serviceApiV3.get<WaapAccessLogDetail>(`${path}?${params.toString()}`, {
          timeout: 150_000,
        });
        return res.data;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404 || status === 501) return null;
        throw e;
      }
    },
  });
}
